import React, { FC, MouseEvent, useCallback, useEffect, useMemo } from 'react'
import {
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { useDispatch, useSelector } from 'react-redux'
import { ClientRiskScoreEnum, isCurrenciesFiat, RiskScoreEnum, TransactionTypeEnum } from '@sparling/financial-common'
import { startCase } from 'lodash'
import { toast } from 'react-toastify'
import { useHistory, useParams } from 'react-router-dom'
import { GreenButton } from '../../../../components/Buttons/GreenButton'
import { getTransactionDetailsData, transactionDetailsLoading } from '../../../../store/selectors'
import { getTransactionDetailsRequest } from '../../../../store/actions'
import { Loader } from '../../../../components/Loader'
import { icons } from '../../../../constants/icons'

const useStyles = makeStyles((theme: Theme) => ({
  topWrap: {
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingTop: theme.spacing(2),
  },
  container: {
    justifyContent: 'space-between',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    marginTop: theme.spacing(2),
    padding: theme.spacing(4),
  },
  sectionName: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
    fontSize: 22,
    color: theme.palette.primary.light,
  },
  field: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignItems: 'center',
    color: theme.palette.primary.light,
    marginBottom: theme.spacing(1),
  },
  fieldTitle: {
    fontSize: 18,
    color: theme.palette.primary.light,
  },
  fieldContent: {
    fontSize: 16,
    wordWrap: 'break-word',
    color: theme.palette.primary.light,
  },
  title: {
    fontSize: 22,
    color: theme.palette.primary.light,
  },
  finalRisk: {
    fontWeight: 600,
    fontSize: 16,
  },
  iconCopy: {
    padding: 0,
    color: theme.palette.secondary.main,
    opacity: 0.6,
    paddingLeft: theme.spacing(1),
    '& :first-child': {
      padding: 0,
      fontSize: 18,
    },
  },
}))

const getRiskColor = (risk: RiskScoreEnum | ClientRiskScoreEnum | undefined | null) => {
  switch (risk) {
    case RiskScoreEnum.SEVERE:
    case RiskScoreEnum.HIGH:
      return 'red'
    case RiskScoreEnum.MEDIUM:
      return 'orange'
    case RiskScoreEnum.LOW:
      return 'green'
    default:
      return 'white'
  }
}

const formatIban = (iban: string | undefined): string => {
  if (!iban || iban.length < 4) return '****'
  return `****${iban.slice(-4)}`
}

const formatAddress = (address: string): string => {
  if (!address || address.length <= 10) return address
  return `${address.slice(0, 5)}......${address.slice(-6)}`
}

export const TransactionDetails: FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { transactionId } = useParams<{ transactionId: string }>()

  const loading = useSelector(transactionDetailsLoading)
  const data = useSelector(getTransactionDetailsData)

  const backHref = () => {
    history.goBack()
  }

  const isFiat = useMemo(
    () => data?.transaction?.currency && isCurrenciesFiat(data?.transaction.currency.toString()),
    [data?.transaction?.currency],
  )

  const copyClipboard = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    if (event.currentTarget.dataset.address)
      navigator.clipboard.writeText(event.currentTarget.dataset.address).then(() => {
        toast('Copied to clipboard!', { type: 'success' })
      })
  }, [])

  useEffect(() => {
    if (transactionId) {
      dispatch(getTransactionDetailsRequest({ id: Number(transactionId) }))
    }
  }, [dispatch, transactionId])

  if (loading) return <Loader />

  return (
    <Grid container>
      <Grid container className={classes.topWrap}>
        <Grid item xs={11}>
          <Typography variant="h1" className={classes.title}>
            Transaction (id: {transactionId})
          </Typography>
        </Grid>
        <Grid container item xs={1} justifyContent={'flex-end'}>
          <GreenButton text="Back" width={80} onClick={backHref} />
        </Grid>
      </Grid>
      <Grid container item xs={12} display="flex" className={classes.container}>
        <Grid container item xs={12}>
          <Typography className={classes.sectionName}>Transaction details</Typography>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={12} md={4} className={classes.field}>
            <Typography className={classes.fieldTitle}>Currency:</Typography>
            <Typography className={classes.fieldContent}>{data?.transaction.currency}</Typography>
          </Grid>
          <Grid item xs={12} md={4} className={classes.field}>
            <Typography className={classes.fieldTitle}>Amount:</Typography>
            <Typography className={classes.fieldContent}>
              {data?.transaction.amount} {data?.transaction.currency}
            </Typography>
          </Grid>
        </Grid>
        {data?.transaction.type === TransactionTypeEnum.WITHDRAWAL && (
          <Grid container item xs={12}>
            <Grid item xs={12} md={6} className={classes.field}>
              <Typography className={classes.fieldTitle}>{`${isFiat ? 'Bank' : 'Gas'} Fees:`}</Typography>
              <Typography className={classes.fieldContent}>{data?.transaction.fee}</Typography>
            </Grid>
          </Grid>
        )}

        <Grid container item xs={12} alignItems={'flex-start'}>
          {isFiat ? (
            <>
              <Grid item xs={12} md={4}>
                <Grid item xs={12} md={6} className={classes.field}>
                  <Typography className={classes.fieldTitle}>Client Bank:</Typography>
                  <Typography className={classes.fieldContent}>{data?.transaction.clientBankName || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={6} className={classes.field}>
                  <Typography className={classes.fieldTitle}>Company Bank:</Typography>
                  <Typography className={classes.fieldContent}>{data?.transaction.companyBankName || '-'}</Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} md={4}>
                <Grid item xs={12} md={6} className={classes.field}>
                  <Typography className={classes.fieldTitle}>Client IBAN:</Typography>
                  <Typography className={classes.fieldContent}>{formatIban(data?.transaction.clientIban)}</Typography>
                  <IconButton
                    className={classes.iconCopy}
                    onClick={copyClipboard}
                    data-address={data?.transaction.clientIban}
                  >
                    <span className="material-icons">{icons.COPY}</span>
                  </IconButton>
                </Grid>
                <Grid item xs={12} md={6} className={classes.field}>
                  <Typography className={classes.fieldTitle}>Company IBAN:</Typography>
                  <Typography className={classes.fieldContent}>{formatIban(data?.transaction.companyIban)}</Typography>
                  <IconButton
                    className={classes.iconCopy}
                    onClick={copyClipboard}
                    data-address={data?.transaction.companyIban}
                  >
                    <span className="material-icons">{icons.COPY}</span>
                  </IconButton>
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12} md={4} className={classes.field}>
                <Typography className={classes.fieldTitle}>Address from:</Typography>
                <Typography className={classes.fieldContent}>
                  {data?.transaction.from ? formatAddress(data?.transaction.from) : '-'}
                </Typography>
                <IconButton className={classes.iconCopy} onClick={copyClipboard} data-address={data?.transaction.from}>
                  <span className="material-icons">{icons.COPY}</span>
                </IconButton>
              </Grid>
              <Grid item xs={12} md={4} className={classes.field}>
                <Typography className={classes.fieldTitle}>Address to:</Typography>
                <Typography className={classes.fieldContent}>
                  {data?.transaction.to ? formatAddress(data?.transaction.to) : '-'}
                </Typography>
                <IconButton className={classes.iconCopy} onClick={copyClipboard} data-address={data?.transaction.to}>
                  <span className="material-icons">{icons.COPY}</span>
                </IconButton>
              </Grid>
            </>
          )}
          <Grid item xs={12} md={4} className={classes.field}>
            <Typography className={classes.fieldTitle}>Transaction type:</Typography>
            <Typography className={classes.fieldContent}>{startCase(data?.transaction.type)}</Typography>
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={12} md={4} className={classes.field}>
            <Typography className={classes.fieldTitle}>Client risk score:</Typography>
            <Typography
              className={classes.fieldContent}
              style={{ fontWeight: 500, color: getRiskColor(data?.transaction.clientRiskScore) }}
            >
              {data?.transaction.clientRiskScore}
            </Typography>
          </Grid>
          {isFiat ? (
            <Grid item xs={12} md={4} className={classes.field}>
              <Typography className={classes.fieldTitle}>Final risk score:</Typography>
              <Typography
                className={classes.finalRisk}
                style={{
                  fontWeight: 500,
                  color: getRiskColor((data?.transaction.finalRiskScore as unknown as RiskScoreEnum) || null),
                }}
              >
                {data?.transaction.finalRiskScore || '-'}
              </Typography>
            </Grid>
          ) : (
            <Grid item xs={12} md={4} className={classes.field}>
              <Typography className={classes.fieldTitle}>KYT score:</Typography>
              <Typography
                className={classes.fieldContent}
                style={{ fontWeight: 500, color: getRiskColor(data?.transaction.kytScore) }}
              >
                {data?.transaction.kytScore}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} md={4} className={classes.field}>
            <Typography className={classes.fieldTitle}>Sumsub score:</Typography>
            <Typography className={classes.fieldContent}>{data?.transaction.sumsubScore || '-'}</Typography>
          </Grid>
        </Grid>
        {!isFiat && (
          <Grid container item xs={12}>
            <Grid item xs={12} className={classes.field}>
              <Typography className={classes.fieldTitle}>Final risk score:</Typography>
              <Typography
                className={classes.finalRisk}
                style={{
                  fontWeight: 500,
                  color: getRiskColor(data?.transaction.finalRiskScore as unknown as RiskScoreEnum),
                }}
              >
                {data?.transaction.finalRiskScore || '-'}
              </Typography>
            </Grid>
          </Grid>
        )}
      </Grid>
      <Grid item xs={12} className={classes.container}>
        <Typography className={classes.sectionName}>Transaction status history</Typography>
        <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.fieldTitle}>Name</TableCell>
                <TableCell className={classes.fieldTitle}>Date</TableCell>
                <TableCell className={classes.fieldTitle}>Log</TableCell>
                <TableCell className={classes.fieldTitle}>Updated By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.statusHistory.map((item, i) => (
                <TableRow key={i}>
                  <TableCell className={classes.fieldContent}>
                    {startCase(item.name).replace('B 2 C 2', 'B2C2')}
                  </TableCell>
                  <TableCell className={classes.fieldContent}>{item.date}</TableCell>
                  <TableCell className={classes.fieldContent}>{item.log}</TableCell>
                  <TableCell className={classes.fieldContent}>{item.updatedBy}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  )
}
